"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PDFViewer } from "@react-pdf/renderer";
import PdfContent from "./pdfcontent";
import { toast } from "react-toastify";
import axios from "axios";
import { useMediaQuery } from "@mui/system";
import { pdf } from "@react-pdf/renderer";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;
interface AnimationData {
  animationId: number;
  animationTagName: string;
  title: string;
  Introduction: string;
  incidencePrevalence: string;
  image1: string;
  causesRiskFactors: string;
  tagTitle: string;
  nameOfTagTitle: string;
  image2: string;
  symptoms: string;
  diagnosis: string;
  treatmentOptions: string;
  callToAction: string;
}

interface Props {
  animationId: number | undefined;
  onClose: () => void;
}

const PdfViewerModal: React.FC<Props> = ({ animationId, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [animationData, setAnimationData] = useState<AnimationData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [_pdfReady, setPdfReady] = useState(false);

  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    if (animationData && isMobile) {
      const generateAndOpenPdf = async () => {
        const blob = await pdf(
          <PdfContent animationData={animationData} />
        ).toBlob();
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank"); // opens in browser PDF viewer
      };

      generateAndOpenPdf();
      handleCloseModal(); // optionally close the modal on mobile
    }
  }, [animationData, isMobile]);

  // Monitor PDF rendering completion
  useEffect(() => {
    if (animationData && pdfContainerRef.current) {
      const observer = new MutationObserver(() => {
        // Check if PDF iframe is loaded
        const pdfIframe = pdfContainerRef.current?.querySelector("iframe");
        if (pdfIframe) {
          try {
            // Wait a bit for the PDF to fully render
            setTimeout(() => {
              setPdfReady(true);
              setLoading(false);
            }, 500);
            observer.disconnect();
          } catch (err) {
            console.error("Error monitoring PDF rendering:", err);
          }
        }
      });

      observer.observe(pdfContainerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
      });

      return () => observer.disconnect();
    }
  }, [animationData]);

  useEffect(() => {
    const savedAnimationId = localStorage.getItem("selectedAnimationId");
    const idToFetch = animationId ?? Number(savedAnimationId);

    if (idToFetch) {
      setIsOpen(true);
      setLoading(true);
      setPdfReady(false);

      const fetchData = async () => {
        try {
          const response = await axios.get(
            `${API_BASE_URL}animation/aimationpdf?languageId=50&animationId=${idToFetch}`
          );

          if (
            response.status >= 200 &&
            response.status < 300 &&
            response.data?.data
          ) {
            setAnimationData(response.data.data);
            // Don't set loading to false here - wait for PDF to render
          } else {
            throw new Error("No data available for the requested animation.");
          }
        } catch (err: any) {
          const msg =
            err?.response?.data?.message || "Failed to fetch PDF details.";
          toast.error(msg);
          setError(msg);
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setError("Invalid animation ID");
      setLoading(false);
    }
  }, [animationId]);

  const handleCloseModal = () => {
    setIsOpen(false);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleCloseModal}
      aria-labelledby="pdf-viewer-modal"
      aria-describedby="modal-to-view-pdf"
      sx={{
        backdropFilter: "blur(3px)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          position: "relative",
          bgcolor: loading ? "transparent" : "background.paper",
          borderRadius: "25px",
          boxShadow: loading ? 0 : 24,
          p: 4,
          width: { xs: "70%", sm: "80%", md: "70%", lg: "70%", xl: "80%" },
          height: { xs: "85%", sm: "80%", md: "70%", lg: "85%", xl: "90%" },
          transition: "0.3s ease-in-out",
        }}
      >
        {!loading && (
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              bgcolor: "#fff",
              "&:hover": {
                bgcolor: "#f44336",
                color: "#fff",
              },
              zIndex: 1000,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {!loading && (
          <Typography variant="h6" sx={{ mb: 2 }}>
            PDF Viewer
          </Typography>
        )}

        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 50,
              backgroundImage: `url(${VISUALIZE__URL}+"link.mp4")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "60px 60px",
              opacity: 0.7,
            }}
          />
        )}

        {error && !loading && (
          <Typography color="error" textAlign="center">
            {error}
          </Typography>
        )}

        {animationData && (
          <div
            ref={pdfContainerRef}
            style={{
              width: "100%",
              height: "90%",
              visibility: loading ? "hidden" : "visible",
              position: "relative",
            }}
          >
            <PDFViewer style={{ width: "100%", height: "100%" }}>
              <PdfContent animationData={animationData} />
            </PDFViewer>
          </div>
        )}

        {!animationData && !error && !loading && (
          <Typography textAlign="center">
            No data available to display.
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default PdfViewerModal;
